import {type ErrorCode} from './errors';
import {ClientSessionData} from "@/app/auth/session-types";
import {createBasepathPath} from "@/lib/utils";
import {UnauthenticatedCode} from "@/lib/types";
import {broadcastAuthEvent} from "@/lib/cross-tab-sync";
import {logError} from "./logging";

/**
 * Simplified API client for Auth0 SDK integration
 *
 * Auth0 Next.js SDK handles all authentication automatically:
 * - Token storage in secure HTTP-only cookies
 * - Token refresh via middleware on every request
 * - Session management through getSession()
 *
 * This client just provides a consistent interface for making API calls.
 * No manual token refresh needed - Auth0 SDK handles it all!
 */

type FetchOptions = RequestInit & {
  params?: Record<string, any>;
}

export class ApiClient {
  /**
   * Build URL with base path and optional query parameters
   */
  private buildUrl(path: string, params?: Record<string, any>): string {
    const url = createBasepathPath(path);
    if (!params || Object.keys(params).length === 0) {
      return url;
    }

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `${url}?${queryString}` : url;
  }

  /**
   * Check if a session endpoint response indicates invalidation and broadcast logout.
   * Only intended to be called with the response from /api/auth/session/.
   */
  private async handleSessionInvalidation(response: Response): Promise<void> {
    if (response.status === 401 || response.status === 403) {
      try {
        const data = await response.clone().json();
        if (data?.code === 'session_invalidated' || response.status === 401) {
          broadcastAuthEvent('logout');
        }
      } catch {
        if (response.status === 401) {
          broadcastAuthEvent('logout');
        }
      }
    }
  }

  /**
   * Fetch the session endpoint and check for invalidation.
   * Returns the session response so callers can inspect the body.
   */
  private async checkSessionValid(): Promise<Response> {
    const url = this.buildUrl('/api/auth/session/');
    const response = await fetch(url, {credentials: 'include'});
    await this.handleSessionInvalidation(response);
    return response;
  }

  /**
   * Main fetch method for API calls.
   * On 401, verifies the session and broadcasts logout if the session is also invalid.
   */
  async fetch(path: string, options: FetchOptions = {}): Promise<Response> {
    const {params, ...fetchOptions} = options;
    const url = this.buildUrl(path, params);

    const response = await fetch(url, {
      ...fetchOptions,
      credentials: 'include',
    });

    if (response.status === 401) {
      await this.checkSessionValid();
    }

    return response;
  }

  /**
   * Fetch method for streaming requests (like Server-Sent Events)
   */
  async fetchStreaming(path: string, options: FetchOptions = {}): Promise<Response> {
    return this.fetch(path, options);
  }

  /**
   * Fetch streaming method that throws errors as specified class
   */
  fetchStreamingThrowAs = <T extends Error>(throwAsClass: new (code: ErrorCode, cause: string) => T) =>
    async (path: string, init?: RequestInit): Promise<Response> => {
      try {
        return await this.fetchStreaming(path, init);
      } catch (error: unknown) {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          throw new throwAsClass('offline:chat' as ErrorCode, 'Network is offline');
        }

        if (error instanceof throwAsClass) {
          throw error;
        }

        const errorMessage = error instanceof Error ? error.message : String(error);
        throw new throwAsClass('bad_request:chat' as ErrorCode, errorMessage);
      }
    };

  /**
   * Fetch method that throws errors as specified class
   */
  fetchThrowAs = <T extends Error>(throwAsClass: new (code: ErrorCode, cause: string) => T) =>
    async (path: string, params?: Record<string, any>): Promise<any> => {
      const response = await this.fetch(path, {params});

      if (!response.ok) {
        const {code, cause} = await response.json();
        throw new throwAsClass(code as ErrorCode, cause);
      }

      return response.json();
    };

  /**
   * Initialize user state from server session.
   * The /api/auth/session endpoint validates the Auth0 session and checks
   * whether the session has been revoked in the database.
   */
  async initializeUserState(): Promise<{ sessionData: ClientSessionData | null, code: UnauthenticatedCode | 'success' }> {
    try {
      const response = await this.checkSessionValid();

      if (response.ok) {
        const data = await response.json();

        if (data?.status === 'success' && data?.user) {
          return {sessionData: data, code: 'success'};
        }

        return {sessionData: null, code: 'unauthenticated'};
      }

      if (response.status === 401) {
        const data = await response.json();
        const unauthenticatedCode = data?.code;
        if (unauthenticatedCode) {
          return {sessionData: null, code: unauthenticatedCode};
        }

        return {sessionData: null, code: 'unauthenticated'};
      }

      logError(new Error("Failed to fetch session"), {
        message: "Failed to fetch session",
        status: response.status
      });
      return {sessionData: null, code: 'unauthenticated'};
    } catch (error: any) {
      logError(error, {
        message: "Error initializing user state"
      });
      return {sessionData: null, code: 'unauthenticated'};
    }
  }
}

// Export a singleton instance
export const apiClient = new ApiClient();
